import {
  afterNextRender,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { TooltipDirective } from '../../../../shared/components/ui/tooltip/tooltip.directive';
import { AppStore } from '../../../../state/app.repository';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ComponentsFrame } from '../components-frame/components-frame';
import { RecordingConsentDialog } from '../recording-consent-dialog/recording-consent-dialog';
import { AppService } from '../../../services/app.service';
import {
  faArrowRightFromLine,
  faBars,
  faGrid2Plus,
  faMicrochipAi,
  faSquareDashedCirclePlus,
  faListCheck,
  faSpinnerThird,
  faDownload,
  faFilePdf,
} from '@fortawesome/pro-duotone-svg-icons';
import { faMarkdown } from '@fortawesome/free-brands-svg-icons/faMarkdown';
import { IconBtn } from '../../../../shared/components/ui/buttons/icon-btn/icon-btn';
import { SquareIconBtn } from '../../../../shared/components/ui/buttons/square-icon-btn/square-icon-btn';
import { TranslatePipe } from '@ngx-translate/core';
import { UsersList } from '../side-panel/users-list/users-list';
import { UserSettingsStore } from '../../../../state/user-settings.repository';
import { Room, RoomsStore } from '../../../../state/rooms.repository';
import { OdinService } from '../../../services/odin.service';
import { UserService } from '../../../services/user.service';
import { TextField } from '../../../../shared/components/form-fields/text-field/text-field';
import { TextFieldAction } from '../../../../shared/components/form-fields/text-field/text-field.types';
import {
  AbstractControl,
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, filter, firstValueFrom, map, of, timeout } from 'rxjs';
import { NotificationService } from '../../../services/notification.service';
import { TranscriptService } from '../../../services/transcript.service';
import { TranscriptSession, TranscriptsStore } from '../../../../state/transcripts.repository';
import { downloadFile } from '../../../../shared/helpers/download-file';
import { sleep } from '../../../../shared/helpers/async';
import { PermissionStore } from '../../../../state/permissions.repository';
import { ActivityLog } from '../activity-log/activity-log';
import { PanelNavBar } from '../panel-nav-bar/panel-nav-bar';
import { ActivityLogStore } from '../../../../state/activity-log.repository';
import { AttendanceExportService } from '../../../services/attendance-export.service';
import { LanguageService } from '../../../services/language.service';
import { formatDate } from '../../../../shared/i18n/date-format';
import { LocalizedDatePipe } from '../../../../shared/pipes/localized-date.pipe';
import { Notice } from '../../../../shared/components/ui/notice/notice';
import { formatCountdown } from '../../../../shared/helpers/duration';

type ActionsTab = 'breakout' | 'activity' | 'transcription';

@Component({
  selector: 'andor-conference-panel',
  standalone: true,
  imports: [
    CommonModule,
    LocalizedDatePipe,
    TranslatePipe,
    MatIconModule,
    TooltipDirective,
    FontAwesomeModule,
    ComponentsFrame,
    IconBtn,
    SquareIconBtn,
    UsersList,
    TextField,
    ReactiveFormsModule,
    ActivityLog,
    RecordingConsentDialog,
    PanelNavBar,
    Notice,
  ],
  templateUrl: './conference-panel.html',
  styleUrl: './conference-panel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConferencePanel {
  protected readonly faBars = faBars;
  protected readonly faGrid2Plus = faGrid2Plus;
  protected readonly faMicrochipAi = faMicrochipAi;
  protected readonly faArrowRightFromLine = faArrowRightFromLine;
  protected readonly faListCheck = faListCheck;
  protected readonly faSpinnerThird = faSpinnerThird;
  protected readonly faDownload = faDownload;
  protected readonly faFilePdf = faFilePdf;
  protected readonly faMarkdown = faMarkdown;

  private _attendanceExport = inject(AttendanceExportService);
  private _language = inject(LanguageService);

  sidePanelClass = toSignal(this.userSettingsStore.getSidePanelClass$());

  // Length limits count the TRIMMED value: the name is trimmed before it is
  // joined (see the trailing button below), so " ab " must not pass as 4
  // chars only to become a 2-char id — which invite links would then reject
  // (parseDeepLink applies the same 3-100 bounds after trimming).
  breakoutRoomNameControl = new FormControl('', [
    Validators.required,
    (control: AbstractControl): ValidationErrors | null => {
      const length = String(control.value ?? '').trim().length;
      if (length < 3) return { minlength: { requiredLength: 3, actualLength: length } };
      if (length > 100) return { maxlength: { requiredLength: 100, actualLength: length } };
      return null;
    },
  ]);

  protected readonly breakoutRoomTrailingButton: TextFieldAction = {
    icon: faSquareDashedCirclePlus,
    click: (value) => {
      if (this.breakoutRoomNameControl.valid) {
        // Trim at creation so padded names round-trip through invite links
        // (the deep-link parser trims). Header clicks in the users list stay
        // verbatim on purpose — those ids must match other peers' data.
        this.joinBreakoutRoom(value.trim());
      }
    },
  };

  @ViewChild('actionsElement') actionsElement?: ElementRef<HTMLDivElement>;
  @ViewChild('actionsContent') actionsContent?: ElementRef<HTMLDivElement>;
  @ViewChild('transcriptList') transcriptListRef?: ElementRef<HTMLDivElement>;
  @ViewChild(UsersList) usersListComponent?: UsersList;
  @ViewChild(ActivityLog) activityLogComponent?: ActivityLog;

  // `null` means the expanded panel is closed. The three tabs are mutually
  // exclusive — clicking a different tab while one is open swaps the
  // content and re-measures the panel height; clicking the active tab
  // collapses the panel.
  activeTab = signal<ActionsTab | null>(null);
  isContentVisible = false;
  actionsHeight: number | null = null;

  // Coalesces re-measures requested within the same frame. Opening/switching a
  // tab (`selectTab`, animate) and the content effects (snap) both fire on
  // tab-open — merging them into a single measure lets the open animate while a
  // later lone line-arrival snaps. `animate` wins when both are requested for
  // the same frame.
  private _measureScheduled = false;
  private _measureAnimate = false;

  // True once the transcription panel has grown to its max height and the list
  // scrolls internally. While capped, appending a line changes neither the
  // panel height nor the list-cap geometry, so the per-line re-measure (an O(n)
  // forced layout over every line) is skipped — we only keep the list pinned to
  // the newest line. Recomputed by `measureAndExpand`, reset on tab switch /
  // close; recording-state changes re-measure via the separate layout effect.
  private _transcriptCapped = false;

  // Last observed transcript length, used to detect a shrink (new recording
  // session, room switch, cleared history). A shrink invalidates the cap latch
  // above — the old, larger content is gone — so we force a full re-measure
  // instead of inheriting the stale cap on the fast path.
  private _lastTranscriptLen = 0;

  // Adapter for the template's existing `isSideBarActionsMenuOpen` consumers
  // — readable getter so we don't have to touch every consumer at once.
  get isSideBarActionsMenuOpen(): boolean {
    return this.activeTab() !== null;
  }

  // Gates the heavy subtrees (components-frame, users-list) so they only
  // mount after the app-stage CSS transition (`--animation-duration: 0.2s`)
  // has finished. Mounting them synchronously competes with the transition
  // for main-thread time and makes the stage resize look truncated.
  contentReady = signal(false);

  // Derived from the global transcription-bot presence — true whenever the
  // bot is in the room, regardless of which tab/user pressed Start. Drives
  // the Stop/Start button label and `active` variant.
  isRecording = computed(() => this.odinService.transcriptionBot() !== null);

  // True while a start/stop HTTP call is in flight (and, for start, until
  // the bot actually joins or the wait times out). Used to disable the
  // toggle button and swap its label to a spinner.
  isTogglingRecording = signal(false);

  // True while the pre-download backlog fetch is in flight. Disables the
  // download button and swaps its icon to a spinner.
  isDownloadingTranscript = signal(false);

  // Bot-presence as an observable so we can `await` the next time it flips
  // to a desired state with a timeout fallback.
  private _botPresent$ = toObservable(this.odinService.transcriptionBot).pipe(
    map((bot) => bot !== null)
  );

  // Tracks the activity-log event stream length so an `effect` can re-measure
  // the expanded actions panel whenever a new entry arrives while the
  // activity tab is open — without it, the outer `actionsHeight` locks at
  // open-time and the growing list spills below the action buttons.
  private _activityEventsCount = toSignal(
    inject(ActivityLogStore).events$.pipe(map((events) => events.length)),
    { initialValue: 0 }
  );

  private _currentRoomId = toSignal(inject(UserSettingsStore).roomId$, { initialValue: '' });

  // Live rooms (synced from the server) so we can read the current room's
  // recording metadata. Recording *activity* is still driven by bot presence
  // (`isRecording`); this adds *who* started it, for the informational
  // "Recording started by …" label.
  private _roomsStore = inject(RoomsStore);
  private _rooms = toSignal(this._roomsStore.rooms$, { initialValue: [] as Room[] });
  private _currentRoom = computed(() =>
    this._rooms().find((r) => r.roomId === this._currentRoomId())
  );

  // Display name of whoever started the current room's recording, for the
  // informational "Recording started by …" label. Purely a label — the Stop
  // control is gated on the `startRecording` permission, so anyone holding it can
  // stop the recording, not just the starter (a recording is never stuck if its
  // starter leaves).
  recordingOwnerName = computed(() => this._currentRoom()?.recording?.startedByName ?? '');

  // Recent transcript sessions, built from the live stream (+ backlog) and
  // persisted locally. The panel shows the most recent stored session for the
  // current room — during a recording that IS the active one (newest startedAt),
  // and after it stops it's still the one to view/export. Keyed off the current
  // room *id* (not the rooms-store entry) so it works for hidden/instant-meeting
  // rooms, which never appear in the rooms store.
  private _transcriptsStore = inject(TranscriptsStore);
  private _transcriptService = inject(TranscriptService);
  private _transcriptSessions = toSignal(this._transcriptsStore.sessions$, {
    initialValue: [] as TranscriptSession[],
  });
  activeTranscriptSession = computed(() => {
    const roomId = this._currentRoomId();
    if (!roomId) return undefined;
    return this._transcriptSessions()
      .filter((s) => s.roomId === roomId)
      .sort((a, b) => b.startedAt - a.startedAt)[0];
  });
  activeTranscript = computed(() => this.activeTranscriptSession()?.messages ?? []);

  /** Ticks every second while a deletion countdown runs (see the effect below). */
  private readonly _now = signal(Date.now());
  /**
   * After a recording ended, the server deletes its transcript at the
   * session's `deletesAt`: `remaining` is the live `mm:ss` until then, `null`
   * once it has passed (only the copy in this browser is left). `null` overall
   * while recording, without a transcript, or for transcripts without a known
   * deadline.
   */
  transcriptRetention = computed(() => {
    const deletesAt = this.activeTranscriptSession()?.deletesAt;
    if (this.isRecording() || deletesAt === undefined) return null;
    if (this.activeTranscript().length === 0) return null;
    const left = deletesAt - this._now();
    return { remaining: left > 0 ? formatCountdown(left) : null };
  });
  /** Which deletion notice shows (`none` / `countdown` / `deleted`); changes far less often than the countdown. */
  private readonly _retentionNotice = computed(() => {
    const retention = this.transcriptRetention();
    if (retention === null) return 'none';
    return retention.remaining === null ? 'deleted' : 'countdown';
  });

  constructor(
    public appStore: AppStore,
    public appService: AppService,
    public userSettingsStore: UserSettingsStore,
    protected odinService: OdinService,
    private userService: UserService,
    private cdr: ChangeDetectorRef,
    private notificationService: NotificationService,
    public permissionStore: PermissionStore
  ) {
    effect(() => {
      if (this.sidePanelClass() === '') {
        this.closeSideBarActionsMenu();
      }
    });

    // Re-measure the expanded actions panel when the activity log gains or
    // loses entries while the activity tab is open. `requestAnimationFrame`
    // defers the read until after Angular has flushed the new row into the
    // DOM so `scrollHeight` reflects the post-update content.
    effect(() => {
      const count = this._activityEventsCount();
      if (this.activeTab() !== 'activity') return;
      void count;
      // Snap: the list already reflowed instantly, so animating the height
      // would glide the whole content block up on every new entry.
      this.scheduleMeasure(false);
    });

    // Transcription tab — line growth. Below the cap the panel grows to fit
    // each new line (cheap: only a handful of lines fit before the cap). Once
    // capped, the panel height and the list's max-height are already final and
    // don't change as more lines arrive, so we skip the O(n) re-measure and only
    // keep the list pinned to its newest line. This is what keeps a long
    // meeting's transcript cheap without a virtual scroller.
    effect(() => {
      const lineCount = this.activeTranscript().length;
      if (this.activeTab() !== 'transcription') return;
      // A shrink means the transcript was reset (new recording session, room
      // switch, cleared history); the old cap no longer applies, so force a full
      // re-measure rather than inheriting it on the fast path.
      const wasReset = lineCount < this._lastTranscriptLen;
      this._lastTranscriptLen = lineCount;
      if (this._transcriptCapped && !wasReset) {
        requestAnimationFrame(() => this.pinTranscriptToNewest());
      } else {
        // Snap so the panel tracks the instantly-reflowed list; the scheduler
        // pins the list to the newest line after measuring.
        this.scheduleMeasure(false);
      }
    });

    // Transcription tab — layout-affecting state. Starting/stopping a recording
    // toggles the info note and swaps the record button for the running indicator,
    // and the deletion notice appears or changes its text after a stop — all of
    // which change the non-list height, so we must re-measure (and re-evaluate
    // the cap) even when the list is capped. The notice's shape, not its ticking
    // countdown, is what matters here.
    effect(() => {
      const toggling = this.isTogglingRecording();
      const recording = this.isRecording();
      const notice = this._retentionNotice();
      if (this.activeTab() !== 'transcription') return;
      void toggling;
      void recording;
      void notice;
      this.scheduleMeasure(false);
    });

    // Drive the deletion countdown: tick every second while the deadline is
    // ahead, stop at it. Nothing runs while recording or without a deadline.
    effect((onCleanup) => {
      const deletesAt = this.activeTranscriptSession()?.deletesAt;
      if (this.isRecording() || deletesAt === undefined) return;
      this._now.set(Date.now());
      if (deletesAt <= Date.now()) return;
      const timer = setInterval(() => {
        const now = Date.now();
        this._now.set(now);
        if (now >= deletesAt) clearInterval(timer);
      }, 1_000);
      onCleanup(() => clearInterval(timer));
    });

    // Re-measure when the breakout form-control toggles between valid and
    // invalid — the andor-text-field reveals its error message inline
    // beneath the input, which grows the natural scrollHeight and would
    // otherwise be clipped at the bottom of the fixed-height panel.
    this.breakoutRoomNameControl.statusChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      if (this.activeTab() === 'breakout') {
        this.scheduleMeasure(true);
      }
    });

    afterNextRender(() => {
      const actionButtons = this.actionsElement?.nativeElement?.querySelector(
        '.action-buttons'
      ) as HTMLElement | null;
      if (actionButtons) {
        this.actionsHeight = actionButtons.offsetHeight;
        this.cdr.detectChanges();
      }
    });

    // Wait one animation frame so the browser has a chance to paint the
    // stage transition start, then schedule the heavy content mount after
    // the CSS animation (200ms) plus a small buffer.
    const destroyRef = inject(DestroyRef);
    const timer = window.setTimeout(() => {
      this.contentReady.set(true);
    }, 240);
    destroyRef.onDestroy(() => clearTimeout(timer));
  }

  // Tab interaction: clicking the active tab collapses the panel; clicking
  // a different tab swaps the content in place. Either way the height is
  // re-measured against the now-mounted content via `requestAnimationFrame`.
  selectTab(tab: ActionsTab): void {
    if (this.activeTab() === tab) {
      this.closeSideBarActionsMenu();
      return;
    }
    this.activeTab.set(tab);
    this.isContentVisible = false;
    // Fresh content: let the first measure recompute the cap from scratch.
    this._transcriptCapped = false;
    // Animate the open/switch. The content effect fires in the same frame with
    // a snap request; coalescing keeps this animated one.
    this.scheduleMeasure(true);
  }

  // Convenience for templates / callers that referred to the breakout tab
  // by the old name. Equivalent to `selectTab('breakout')`.
  createBreakoutRoom() {
    this.selectTab('breakout');
  }

  closeSideBarActionsMenu() {
    // Closing: hide content first, then collapse.
    this.isContentVisible = false;
    this.activeTab.set(null);
    this._transcriptCapped = false;

    // Collapse to just the action buttons height.
    const actionButtons = this.actionsElement?.nativeElement?.querySelector(
      '.action-buttons'
    ) as HTMLElement;
    this.actionsHeight = actionButtons?.offsetHeight || 50;
  }

  // Schedules a single re-measure for the next frame, coalescing every request
  // made in the current frame. `animate: true` (open/switch) wins over any
  // `false` (content growth) requested for the same frame, so the open still
  // animates while a later lone line-arrival snaps. After measuring, pins the
  // transcript list / activity log to its newest line — both scroll internally
  // only once `measureAndExpand` has capped them, so the pin must come after.
  private scheduleMeasure(animate: boolean): void {
    this._measureAnimate ||= animate;
    if (this._measureScheduled) return;
    this._measureScheduled = true;
    requestAnimationFrame(() => {
      this._measureScheduled = false;
      const animate = this._measureAnimate;
      this._measureAnimate = false;
      this.measureAndExpand(animate);
      if (this.activeTab() === 'transcription') this.pinTranscriptToNewest();
      if (this.activeTab() === 'activity') this.activityLogComponent?.scrollToNewest();
    });
  }

  // Keeps the transcript list scrolled to its newest line. One forced layout
  // read; the only per-line cost left on the capped fast path.
  private pinTranscriptToNewest(): void {
    const list = this.transcriptListRef?.nativeElement;
    if (list && this.activeTranscript().length > 0) list.scrollTop = list.scrollHeight;
  }

  private measureAndExpand(animate = true) {
    if (!this.actionsContent?.nativeElement || !this.actionsElement?.nativeElement) {
      return;
    }
    const actionsEl = this.actionsElement.nativeElement;

    // Measure the transcript list at its natural height: clear any max-height a
    // previous pass set so `scrollHeight` reflects the full content (mirrors the
    // activity log's unconstrained host).
    const transcriptList = this.actionsContent.nativeElement.querySelector(
      '.transcript-list'
    ) as HTMLElement | null;
    if (transcriptList) transcriptList.style.maxHeight = '';

    // scrollHeight reads the full natural height of the active tab's
    // content even while it's still `visibility: hidden`.
    const contentHeight = this.actionsContent.nativeElement.scrollHeight;
    const actionButtons = this.actionsElement.nativeElement.querySelector(
      '.action-buttons'
    ) as HTMLElement;
    const actionsBarHeight = actionButtons?.offsetHeight || 50;

    // Cap the expanded panel at `right-side-panel height − the actions'
    // top + bottom margins`. That makes the gap above the panel mirror the
    // existing bottom margin (`.actions { margin: 0.3rem }`) so the
    // expanded actions sit symmetrically inside `.right-side-panel`. Long
    // content (activity log with many entries) hits this cap; the
    // activity-log itself is `overflow-y: auto` so it scrolls internally.
    const panel = this.actionsElement.nativeElement.closest(
      '.right-side-panel'
    ) as HTMLElement | null;
    // `clientHeight` excludes the panel's 1px borders so the cap is
    // measured against the panel's inner area — required for the top and
    // bottom gaps to come out symmetric. `getBoundingClientRect().height`
    // would include the borders and skew us by 2px.
    const panelInnerHeight = panel?.clientHeight ?? 0;
    const cs = getComputedStyle(this.actionsElement.nativeElement);
    const verticalMargin = (parseFloat(cs.marginTop) || 0) + (parseFloat(cs.marginBottom) || 0);
    const maxHeight = panelInnerHeight > 0 ? panelInnerHeight - verticalMargin : Infinity;

    // Content-driven growth (new transcript/activity lines) reflows the DOM
    // instantly. Snap the height to match; otherwise the SCSS `transition:
    // height` glides the whole top-anchored content block up on every message.
    // The animated transition is kept for open/close/tab-switch.
    if (!animate) actionsEl.style.transition = 'none';

    this.actionsHeight = Math.min(contentHeight + actionsBarHeight, maxHeight);

    // When the activity tab is shown and the cap kicked in, hand the cap
    // down to the `<andor-activity-log>` host so the component's internal
    // scrolling list fits inside the panel instead of overflowing. Applied
    // *after* the cap, never before — otherwise shrinking the host first
    // would shrink the measured scrollHeight and lock the panel at a tiny
    // size.
    const activityLogHost = this.actionsContent.nativeElement.querySelector(
      'andor-activity-log'
    ) as HTMLElement | null;
    if (activityLogHost) {
      if (
        this.activeTab() === 'activity' &&
        this.actionsHeight < contentHeight + actionsBarHeight
      ) {
        // `offsetTop` is relative to `.actions` (the nearest positioned
        // ancestor) and naturally folds in everything above the host —
        // `.actions-content`'s top padding, the `.actions-header` (title +
        // icon buttons), and any margins — so we don't have to keep a
        // magic-number list in sync with the template.
        const hostTop = activityLogHost.offsetTop;
        const contentPaddingBottom =
          parseFloat(getComputedStyle(this.actionsContent.nativeElement).paddingBottom) || 0;
        activityLogHost.style.maxHeight = `${Math.max(
          0,
          this.actionsHeight - actionsBarHeight - hostTop - contentPaddingBottom
        )}px`;
      } else {
        activityLogHost.style.maxHeight = '';
      }
    }

    // Same treatment for the transcript list, but it's only one part of the
    // content (the Download button, separator and record controls sit below it).
    // So give it the leftover height — the capped panel minus everything that
    // isn't the list — and it scrolls internally while the controls stay put.
    if (transcriptList) {
      const capped =
        this.activeTab() === 'transcription' &&
        this.actionsHeight < contentHeight + actionsBarHeight;
      if (capped) {
        const nonListHeight = contentHeight - transcriptList.scrollHeight;
        transcriptList.style.maxHeight = `${Math.max(
          0,
          this.actionsHeight - actionsBarHeight - nonListHeight
        )}px`;
      } else {
        transcriptList.style.maxHeight = '';
      }
      // Latch the capped state so the line-growth effect can take the fast path
      // (skip re-measuring) once the panel can't grow any further.
      this._transcriptCapped = capped;
    } else if (this.activeTab() === 'transcription') {
      // Transcription tab open but the list isn't rendered yet (no lines / just
      // the "Waiting…" placeholder) — nothing is capped.
      this._transcriptCapped = false;
    }
    this.cdr.detectChanges();

    if (!animate) {
      // Flush the new height without animating, then restore the SCSS
      // transition so the next open/close/tab-switch animates again.
      void actionsEl.offsetHeight;
      actionsEl.style.transition = '';
    }

    // Reveal the content only after the height transition finishes so the
    // fade-in doesn't compete with the panel growing.
    const computedStyle = getComputedStyle(this.actionsElement.nativeElement);
    const transitionDuration =
      parseFloat(computedStyle.getPropertyValue('--animation-duration')) || 0.3;
    setTimeout(() => {
      this.isContentVisible = true;
      this.cdr.detectChanges();
    }, transitionDuration * 1000);
  }

  joinBreakoutRoom(roomId: string) {
    this.userService.updateUserData({ breakoutRoomId: roomId });
    this.closeSideBarActionsMenu();

    // Scroll to the room header after the list updates
    setTimeout(() => {
      this.usersListComponent?.scrollToRoom(roomId);
    }, 100);
  }

  openSidePanel() {
    this.userSettingsStore.update({ usersListExpanded: true });
  }

  closeSidePanel() {
    this.userSettingsStore.update({ usersListExpanded: false });
  }

  async toggleRecording() {
    if (this.isTogglingRecording()) return;
    this.isTogglingRecording.set(true);
    try {
      if (this.isRecording()) {
        await this.notificationService.stopRecording();
        // Hold the loading state until the bot actually leaves the room (the
        // server-side stop is async), or until 10s passes. Only then does
        // `isRecording()` flip false. If the stop request itself failed it
        // throws above — we never reach here, so the button correctly stays in
        // the "Stop Recording" state rather than falsely toggling off. The
        // transcript stays visible/exportable from the local store.
        await this.waitForBotPresence(false, 10_000);
      } else {
        await this.notificationService.startRecording();
        // Hold the loading state until the bot actually appears in the room
        // (signalled by `transcriptionBot` flipping), or until 10s passes —
        // whichever comes first. After that, `isRecording()` flips true.
        await this.waitForBotPresence(true, 10_000);
        if (this.odinService.transcriptionBot() === null) {
          // The server accepted the start, so the recording counts as running
          // there, but the bot never reached this room: a bot-side problem.
          console.warn(
            'Recording started on the server, but the transcription bot did not join within 10s',
            { roomId: this._currentRoomId() }
          );
        }
      }
    } catch (error) {
      console.error('Failed to toggle recording', error);
    } finally {
      this.isTogglingRecording.set(false);
    }
  }

  // Resolves when `transcriptionBot()` matches `expected` (true for joined,
  // false for left). Returns early if already in that state. Falls through
  // silently on timeout so the UI can recover even if the bot never shows.
  private async waitForBotPresence(expected: boolean, timeoutMs: number): Promise<void> {
    if ((this.odinService.transcriptionBot() !== null) === expected) return;
    await firstValueFrom(
      this._botPresent$.pipe(
        filter((present) => present === expected),
        timeout(timeoutMs),
        catchError(() => of(null))
      )
    );
  }

  exportActivityPdf(): void {
    this._attendanceExport.exportAsPdf();
  }

  exportActivityMarkdown(): void {
    this._attendanceExport.exportAsMarkdown();
  }

  async downloadTranscript() {
    if (this.isDownloadingTranscript()) return;
    const roomId = this._currentRoomId();
    if (roomId) {
      this.isDownloadingTranscript.set(true);
      try {
        // Heal the local log from the authoritative Cortex snapshot before
        // building the file, so live lines the room frames missed make it into
        // the download. Best-effort: the server serves the running session or
        // the post-stop grace record until the transcript is deleted
        // (RECORDING_RETENTION_MS) and 404s fast otherwise; raced with a
        // timeout so a dead network can't wedge the button.
        await Promise.race([this._transcriptService.heal(roomId), sleep(4_000)]);
      } finally {
        this.isDownloadingTranscript.set(false);
      }
    }
    // Re-read after the merge — the store updates synchronously.
    const session = this.activeTranscriptSession();
    if (!session || session.messages.length === 0) return;
    const locale = this._language.locale();
    const time = (value: number | string) => formatDate(value, 'timestamp', locale);
    const header = `# ${session.roomName} — recorded ${time(session.startedAt)} (started by ${session.startedByName})`;
    const lines = session.messages.map(
      (m) => `[${time(m.timestamp)}] ${m.senderName}: ${m.content}`
    );
    const blob = new Blob([[header, '', ...lines].join('\n')], {
      type: 'text/plain;charset=utf-8',
    });
    const safeName = session.roomName.replace(/[^\w.-]+/g, '_');
    const stamp = new Date(session.startedAt).toISOString().replace(/[:.]/g, '-');
    downloadFile(blob, `transcript-${safeName}-${stamp}.txt`);
  }
}
